import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { useCallback } from 'react';
import dayjs from 'dayjs';
import { DatePicker } from "antd"


import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AiOutlineClose } from 'react-icons/ai';
import { IoIosCloseCircle } from 'react-icons/io';
import { toast } from 'react-toastify';

dayjs.extend(customParseFormat);

const EnrolledStudentsModal = ({
  isOpen,
  onRequestClose,
  formData,
  handleChange,
  handleSelectChange,
  handleRemoveTag,
  handleSubmit,
  selectedCourses,
  dropdownValue,
  courses,
  districts,
  isUpdate,
  isUpload,
  timer,
  selectedStudent,
  canOnlySelectHigherInterest,
  loadingSubmit,
}) => {
  // ── Local mirror of the follow-up date so the picker never snaps back ──
  const [followUp, setFollowUp] = useState(null);

  // Sync local mirror from parent whenever the modal opens or the parent
  // formData changes (e.g. after openModal populates it).
  useEffect(() => {
    if (!isOpen) return;
    const parsed = parseFollowUp(formData.next_follow_up_date);
    setFollowUp(parsed);
    // If the stored value was a zero-date, push an empty string up so the
    // submitted payload is clean.
    const raw = formData.next_follow_up_date;
    if (raw && /^0{4}-0{2}-0{2}/.test(String(raw))) {
      emitFollowUpChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, formData.next_follow_up_date]);


  // ── Date/time helpers ────────────────────────────────────────────────
  const getMinDateTime = () => {
    const now = new Date();
    now.setSeconds(0, 0);
    now.setMinutes(now.getMinutes() + 1);
    return now;
  };

  // Accept "YYYY-MM-DD HH:mm:ss", ISO, or a dayjs object.
  // Accept "YYYY-MM-DD HH:mm:ss", ISO, or a dayjs object.
  // Treat MySQL zero-dates (e.g. "0000-00-00 00:00:00.000000") as empty.
  const parseFollowUp = (value) => {
    if (!value) return null;

    if (dayjs.isDayjs(value)) {
      return value.isValid() && value.year() > 0 ? value : null;
    }

    const str = String(value).trim();

    // Reject zero / invalid MySQL datetimes like:
    //   "0000-00-00 00:00:00", "0000-00-00", "0000-00-00 00:00:00.000000"
    if (/^0{4}-0{2}-0{2}/.test(str)) return null;

    const strict = dayjs(str, 'YYYY-MM-DD HH:mm:ss', true);
    if (strict.isValid()) return strict;

    const loose = dayjs(str);
    return loose.isValid() ? loose : null;
  };

  const emitFollowUpChange = (dayjsVal) => {
    const str = dayjsVal ? dayjsVal.format('YYYY-MM-DD HH:mm:ss') : '';
    handleChange({ target: { name: 'next_follow_up_date', value: str } });
  };

  // ── antd disabledDate / disabledTime ────────────────────────────────
  // Block any calendar day strictly before today.
  const disabledDate = (current) => {
    if (!current) return false;
    return current.endOf('day').isBefore(dayjs().startOf('day'));
  };

  // Block past hours/minutes/seconds ONLY when the chosen day is today.
  const disabledTime = useCallback((current) => {
    // Logic remains the same
    if (!current || !current.isSame(dayjs(), 'day')) {
      return { disabledHours: () => [], disabledMinutes: () => [] };
    }
    const now = dayjs();
    return {
      disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
      disabledMinutes: (selectedHour) => {
        if (selectedHour !== now.hour()) return [];
        return Array.from({ length: now.minute() + 1 }, (_, i) => i);
      },
    };
  }, []); // Dependencies fixed to empty, because 'now' is recalculated internally

  // ── Submit guard ────────────────────────────────────────────────────
  const handleFormSubmit = (e) => {
    const current = followUp || parseFollowUp(formData.next_follow_up_date);
    if (current && current.isBefore(dayjs())) {
      e.preventDefault();
      const bumped = dayjs(getMinDateTime());
      setFollowUp(bumped);
      emitFollowUpChange(bumped);
      toast.warning('Next follow-up date/time cannot be in the past.');
      return;
    }
    handleSubmit(e);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Student Details"
      className="mx-auto my-10 mt-[100px] max-h-[85vh] max-w-full sm:max-w-4xl bg-white rounded-lg shadow-lg overflow-auto"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50"
    >
      <div className="sticky top-0 z-10 bg-white px-8 py-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">
            {isUpdate ? 'Edit Student' : isUpload ? 'Add Student' : 'Student Details'}
          </h2>
          {(isUpdate || isUpload) && (
            <div className="text-2xl font-medium bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 py-1 rounded-lg shadow-md">
              {timer}
            </div>
          )}
          <button
            onClick={onRequestClose}
            className="text-gray-500 hover:text-gray-800 transition-colors"
          >
            <AiOutlineClose size={22} />
          </button>
        </div>
      </div>

      <form onSubmit={handleFormSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 sm:p-8">
        {/* ...all other fields identical to your last version... */}

        <div className="flex flex-col">
          <label className="flex items-center">
            Student Name<span className="ml-1 text-red-600">*</span>
          </label>
          <input
            type="text"
            name="student_name"
            value={formData.student_name || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
            required
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Gender</label>
          <select
            name="gender"
            value={formData.gender || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Father Name</label>
          <input
            type="text"
            name="father_name"
            value={formData.father_name || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">
            Mobile Number 1<span className="ml-1 text-red-600">*</span>
          </label>
          <input
            type="tel"
            name="mobile_number1"
            value={formData.mobile_number1 || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value) && value.length <= 10) {
                handleChange(e);
              }
            }}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={true}
            required
            pattern="\d{10}"
            title="Mobile number must be exactly 10 digits."
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Mobile Number 2:</label>
          <input
            type="tel"
            name="mobile_number2"
            value={formData.mobile_number2 || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value) && value.length <= 10) {
                handleChange(e);
              }
            }}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
            pattern="\d{10}"
            title="Mobile number must be exactly 10 digits."
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Email:</label>
          <input
            type="email"
            name="email"
            value={formData.email || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Aadhar Number:</label>
          <input
            type="text"
            name="aadhar_number"
            value={formData.aadhar_number || ''}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/-/g, '');
              const formattedValue = rawValue
                .replace(/\D/g, '')
                .replace(/(\d{4})(?=\d)/g, '$1-');
              if (formattedValue.length <= 14) {
                const syntheticEvent = { target: { name: 'aadhar_number', value: formattedValue } };
                handleChange(syntheticEvent);
              }
            }}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
            pattern="(\d{4}-\d{4}-\d{4})"
            title="Aadhar number must be in the format XXXX-XXXX-XXXX."
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">State</label>
          <input
            type="text"
            name="state"
            value={formData.state || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">District</label>
          <select
            name="district"
            value={formData.district || ''}
            onChange={handleChange}
            className="p-2 bg-slate-100 border rounded cursor-not-allowed"
            disabled={true}
          >
            <option value="">Select District</option>
            {districts.map((district, index) => (
              <option key={index} value={district}>
                {district}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">City:</label>
          <input
            type="text"
            name="city"
            value={formData.city || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Address:</label>
          <input
            type="text"
            name="address"
            value={formData.address || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Pincode:</label>
          <input
            type="text"
            name="pincode"
            value={formData.pincode || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value) && value.length <= 6) {
                handleChange(e);
              }
            }}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
            pattern="\d{6}"
            title="Pincode must be exactly 6 digits."
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Native District</label>
          <input
            type="text"
            name="native_district"
            value={formData.native_district || ''}
            onChange={handleChange}
            placeholder="Enter native district"
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Native Address</label>
          <input
            type="text"
            name="native_address"
            value={formData.native_address || ''}
            onChange={handleChange}
            placeholder="Enter native address"
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Course</label>
          <input
            type="text"
            name="course"
            value={formData.course || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">College</label>
          <input
            type="text"
            name="clg_name"
            value={formData.clg_name || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Intermediate Percentage/CGPA</label>
          <input
            type="text"
            name="inter_percentage"
            value={formData.inter_percentage || ''}
            onChange={(e) => {
              const value = e.target.value;
              const regex = /^(\d+(\.\d*)?|\.\d+)?$/;
              if (regex.test(value) || value === '') {
                handleChange(e);
              }
            }}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          />
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">Program Type</label>
          <select
            name="program_type"
            value={formData.program_type || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          >
            <option value="">Select Program Type</option>
            <option value="UG">UG</option>
            <option value="PG">PG</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label className="flex items-center">UG/PG Course</label>
          <select
            value={dropdownValue || ''}
            onChange={handleSelectChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
          >
            <option value="">Select Course</option>
            {courses.map((course) => (
              <option key={course} value={course}>
                {course}
              </option>
            ))}
          </select>
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedCourses.map((course) => (
              <div key={course} className="flex items-center bg-blue-100 rounded-full px-3 py-1">
                <span>{course}</span>
                <button
                  type="button"
                  className="ml-2 text-red-500"
                  disabled={!(isUpdate || isUpload)}
                  onClick={() => handleRemoveTag(course)}
                >
                  <IoIosCloseCircle className="text-blue-800 w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col">
          <label className="flex items-center text-lg font-semibold text-gray-700 mb-2">
            Call Duration
          </label>
          <div className="flex flex-row space-x-2">
            <div className="relative">
              <input
                type="text"
                name="hours"
                value={(formData.hours || '0').padStart(2, '0')}
                className="h-10 bg-gray-100 border border-gray-300 text-gray-900 rounded-md w-16 text-center shadow-sm focus:outline-none cursor-not-allowed"
                disabled
              />
              <span className="absolute right-2 top-2 text-gray-500 font-bold">h</span>
            </div>
            <div className="flex items-center text-gray-500 font-bold">:</div>
            <div className="relative">
              <input
                type="text"
                name="minutes"
                value={(formData.minutes || '0').padStart(2, '0')}
                className="h-10 bg-gray-100 border border-gray-300 text-gray-900 rounded-md w-16 text-center shadow-sm focus:outline-none cursor-not-allowed"
                disabled
              />
              <span className="absolute right-2 top-2 text-gray-500 font-bold">m</span>
            </div>
            <div className="flex items-center text-gray-500 font-bold">:</div>
            <div className="relative">
              <input
                type="text"
                name="seconds"
                value={(formData.seconds || '0').padStart(2, '0')}
                className="h-10 bg-gray-100 border border-gray-300 text-gray-900 rounded-md w-16 text-center shadow-sm focus:outline-none cursor-not-allowed"
                disabled
              />
              <span className="absolute right-2 top-2 text-gray-500 font-bold">s</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col col-span-1 sm:col-span-2">
          <label className="flex items-center">
            Call Status<span className="ml-1 text-red-600">*</span>
          </label>
          <select
            name="call_status"
            value={formData.call_status || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${(selectedStudent?.is_interested === 'Interested' && isUpdate) || !(isUpdate || isUpload)
              ? 'cursor-not-allowed'
              : ''
              }`}
            disabled={(selectedStudent?.is_interested === 'Interested' || selectedStudent?.is_interested === 'Most Interested' && isUpdate) || !(isUpdate || isUpload)}
            required
          >
            <option value="">Select Status</option>
            <option value="Contacted">Contacted</option>
            <option value="Not Answered">Not Answered</option>
          </select>
        </div>

        {/* ── Next Follow-Up Date & Time — antd DatePicker, always editable ── */}
        <div className="flex flex-col col-span-1 sm:col-span-2">
          <label className="flex items-center mb-1 font-medium text-gray-700">
            Next Follow-Up Date & Time
          </label>
          <DatePicker
            showTime={{
              hideDisabledOptions: true,
            }}

            needConfirm={false}
            format="DD/MM/YYYY hh:mm A"
            value={followUp}
            onChange={(date) => {
              // Keep local mirror in sync so the input shows exactly
              // what was picked, regardless of any parent parsing.
              setFollowUp(date || null);
              emitFollowUpChange(date || null);
            }}
            disabledDate={disabledDate}
            disabledTime={disabledTime}
            placeholder="Select follow-up date & time"
            style={{ width: '100%' }}
            allowClear
            // Explicitly keep the field editable (never disable it here).
            inputReadOnly={false}
          />
          <p className="text-xs text-gray-500 mt-1">
            Past dates and times are disabled. Earliest selectable:{' '}
            {dayjs(getMinDateTime()).format('DD/MM/YYYY hh:mm A')}.
          </p>
        </div>

        <div className="flex flex-col col-span-1 sm:col-span-2">
          <label className="flex items-center">Remarks:</label>
          <textarea
            name="remarks"
            value={formData.remarks || ''}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isUpdate || isUpload) && 'cursor-not-allowed'}`}
            disabled={!(isUpdate || isUpload)}
            rows={2}
          />
        </div>

        <div className="flex flex-col col-span-1 sm:col-span-2">
          <div className="flex flex-wrap gap-y-2">
            <label className="flex items-center mr-4">
              <input
                type="radio"
                name="is_interested"
                value="Not Interested"
                checked={formData.is_interested === 'Not Interested'}
                onChange={handleChange}
                className="mr-2"
                disabled={
                  formData.is_interested === "Most Interested" ||
                  formData.call_status === 'Not Answered' ||
                  formData.call_status === '' ||
                  !(isUpdate || isUpload) ||
                  (canOnlySelectHigherInterest && formData.is_interested !== 'Not Interested')
                }
              />
              Not Interested
            </label>
            <label className="flex items-center mr-4">
              <input
                type="radio"
                name="is_interested"
                value="Interested"
                checked={formData.is_interested === 'Interested'}
                onChange={handleChange}
                className="mr-2"
                disabled={
                  formData.call_status === 'Not Answered' ||
                  formData.call_status === '' ||
                  !(isUpdate || isUpload) ||
                  (canOnlySelectHigherInterest &&
                    (formData.is_interested === 'Not Interested' ||
                      formData.is_interested === 'Call Back' ||
                      formData.is_interested === 'Others'))
                }
              />
              Interested
            </label>
            <label className="flex items-center mr-4">
              <input
                type="radio"
                name="is_interested"
                value="Most Interested"
                checked={formData.is_interested === 'Most Interested'}
                onChange={handleChange}
                className="mr-2"
                disabled={
                  formData.call_status === 'Not Answered' ||
                  formData.call_status === '' ||
                  !(isUpdate || isUpload)
                }
              />
              Most Interested
            </label>
            <label className="flex items-center mr-4">
              <input
                type="radio"
                name="is_interested"
                value="Call Back"
                checked={formData.is_interested === 'Call Back'}
                onChange={handleChange}
                className="mr-2"
                disabled={
                  formData.call_status === 'Not Answered' ||
                  formData.call_status === '' ||
                  !(isUpdate || isUpload) ||
                  (canOnlySelectHigherInterest && formData.is_interested !== 'Call Back')
                }
              />
              Call Back
            </label>
            <label className="flex items-center mr-4">
              <input
                type="radio"
                name="is_interested"
                value="Others"
                checked={formData.is_interested === 'Others'}
                onChange={handleChange}
                className="mr-2"
                disabled={
                  formData.call_status === 'Not Answered' ||
                  formData.call_status === '' ||
                  !(isUpdate || isUpload) ||
                  (canOnlySelectHigherInterest && formData.is_interested !== 'Others')
                }
              />
              Others
            </label>
          </div>

          <div className="flex items-center mt-4">
            {formData.is_interested === 'Call Back' && (
              <div className="mt-2 flex items-center justify-center">
                <label className="block mb-2">
                  Call Back Reason<span className="ml-1 text-red-600">*</span>:
                </label>
                <select
                  name="call_back_reason"
                  value={formData.call_back_reason || ''}
                  onChange={handleChange}
                  className="p-2 border ml-4 border-gray-300 rounded"
                  required={formData.is_interested === 'Call Back'}
                >
                  <option value="">Select Reason</option>
                  <option value="IPE Exams">IPE Exams</option>
                  <option value="Jee Mains Results">JEE Mains Results</option>
                  <option value="Jee Mains-2 Results">JEE Mains-2 Results</option>
                  <option value="Eamcet Results">EAMCET Results</option>
                  <option value="IPE Results">IPE Results</option>
                  <option value="Supply Exams">Supply Exams</option>
                  <option value="Supply Results">Supply Results</option>
                  <option value="EOD">EOD</option>
                </select>
              </div>
            )}

            {formData.is_interested === 'Others' && (
              <div className="mt-2 flex items-center justify-center">
                <label className="block mb-2">
                  Other Reason<span className="ml-1 text-red-600">*</span>:
                </label>
                <select
                  name="other_reasons"
                  value={formData.other_reasons || ''}
                  onChange={handleChange}
                  className="p-2 border ml-4 border-gray-300 rounded"
                  required={formData.is_interested === 'Others'}
                >
                  <option value="">Select Reason</option>
                  <option value="Wrong Number">Wrong Number</option>
                  <option value="Not Reachable">Not Reachable</option>
                  <option value="Number Not Working">Number Not Working</option>
                  <option value="Call Declined">Call Declined</option>
                  <option value="Busy">Busy</option>
                  <option value="Switched Off">Switched Off</option>
                  <option value="Incoming Calls Stopped">Incoming Calls Stopped</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {(isUpdate || isUpload) && (
          <div className="flex flex-col col-span-1 sm:col-span-2">
            <button
              type="submit"
              className={`bg-blue-600 text-white px-4 py-2 rounded ${loadingSubmit && 'cursor-not-allowed'}`}
              disabled={loadingSubmit}
            >
              {loadingSubmit ? 'Submitting...' : isUpdate ? 'Update Student' : 'Add Student'}
            </button>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EnrolledStudentsModal;